"use client";

import React from "react";
import { CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function WaitlistForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4">
                    <CircleCheck className="size-8" />
                </div>
                <h3 className="text-2xl font-serif font-medium text-foreground">Anmeldung erfolgreich</h3>
                <p className="text-muted-foreground max-w-md">{successMessage}</p>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
                            <FormLabel>Vollständiger Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Anna Müller" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
                            <FormLabel>E‑Mail Adresse</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="anna@example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
                            <FormLabel>Telefon (optional)</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="+41 79 123 45 67" {...field} />
                            </FormControl>
                            <FormDescription>Falls ich dich für Rückfragen kontaktieren soll.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="interest_area" render={({
        field
      }) => <FormItem>
                            <FormLabel>Interessensbereich</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Bitte wähle" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {["Traumaheilung", "Stressreduktion", "Meditation", "Energiearbeit", "Sonstiges"].map((opt, i) => <SelectItem key={i} value={opt} data-index={i}>
                                            {opt}
                                        </SelectItem>)}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
                        <FormLabel>Nachricht (optional)</FormLabel>
                        <FormControl>
                            <textarea className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 resize-y" placeholder="Möchtest du mir etwas mitteilen?" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                    {submitError}
                </div>}

            <Button type="submit" className="w-full h-12 text-base" disabled={isSubmitting}>
                {isSubmitting ? <>
                        <Loader className="mr-2 size-5 animate-spin" />
                        Wird gesendet...
                    </> : "Auf die Warteliste setzen"}
            </Button>
        </Form>;
}
export default function AufDieWartelisteSetzen() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="auf-die-warteliste-setzen" className="py-24 md:py-32 bg-background relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_center,var(--color-primary)_0%,transparent_20%)] opacity-[0.03] pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10">
                <div ref={ref} className={`max-w-3xl mx-auto transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    <div className="bg-card rounded-2xl shadow-xl border border-border/40 p-8 md:p-12">
                        <div className="text-center mb-10 space-y-4">
                            <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 border-none px-4 py-1.5 text-sm font-medium tracking-wide">Geplant 2027<br /></Badge>
                            <h2 className="text-3xl md:text-4xl font-serif font-medium text-foreground tracking-tight">
                                Warteliste Anmeldung
                            </h2>
                            <p className="text-muted-foreground text-lg max-w-xl mx-auto leading-relaxed"> Sichere dir deinen Platz für meine kommenden Healing Retreats in Provence, Frankreich. Ich informiere dich exklusiv, sobald die Buchung öffnet.</p>
                            <p className="text-primary font-medium text-lg">
                                Investition: Ab CHF 1'850
                            </p>
                        </div>

                        <FormProvider formId="waitlist_form_retreats" sectionName="auf-die-warteliste-setzen" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            interest_area: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: "Bitte gib deinen vollständigen Namen an"
            },
            email: {
              required: "Bitte gib deine E-Mail-Adresse an",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Bitte gib eine gültige E-Mail-Adresse ein"
              }
            }
          }} submitText="Auf die Warteliste setzen" successMessage="Vielen Dank für dein Vertrauen. Ich habe dich erfolgreich auf meine Warteliste gesetzt und werde dich kontaktieren, sobald es Neuigkeiten zu meinen Retreats gibt.">
                            <WaitlistForm />
                        </FormProvider>
                    </div>
                </div>
            </div>
        </section>;
}